import 'owl.carousel/dist/assets/owl.carousel.css';

import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { RequireAuth, WithOutAuth, CallStateCheck } from './Middleware';

import BasePage from "./pages/BasePage";
import Home from "./pages/Home";
import ScrollToTop from './pages/ScrollToTop';
import PreLoader from './pages/PreLoader';

// Lazy-loaded Pages & Modules
const Login = lazy(() => import("./pages/Auth/Login"));
const Register = lazy(() => import("./pages/Auth/Register"));
const ForgotPassword = lazy(() => import("./pages/Auth/ForgotPassword"));
const ForgotOTP = lazy(() => import("./pages/Auth/ForgotPassword/OtpVerify"));
const NewPassword = lazy(() => import("./pages/Auth/ForgotPassword/NewPassword"));
const OTP = lazy(() => import("./pages/Auth/OTP"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Contact = lazy(() => import("./pages/Contact"));
const FindMatch = lazy(() => import("./pages/FindMatch"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const NotFound = lazy(() => import('./pages/NotFound'));
const Identification = lazy(() => import("./pages/Auth/Identification"));
const Preferences = lazy(() => import('./pages/Auth/Preferences'));
const ViewProfile = lazy(() => import('./pages/ViewProfile'));
const Chat = lazy(() => import('./pages/Chat'));
const VideoCall = lazy(() => import('./pages/Call/VideoCall'));
const AudioCall = lazy(() => import('./pages/Call/AudioCall'));
const GoLiveStream = lazy(() => import('./pages/Call/GoLiveStream'));
const PrivateVideoRoomScreen = lazy(() => import('./pages/Call/PrivateVideoRoomScreen'));
const Payment = lazy(() => import('./pages/Payment'));
const PaypalPayment = lazy(() => import('./pages/Payment/PaypalPayment'));
const PaymentStatus = lazy(() => import('./pages/Payment/PaymentStatus'));
const Blogs = lazy(() => import('./pages/Blogs'));
const Connections = lazy(() => import('./pages/Connections'));
const Notification = lazy(() => import('./pages/Notification'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const ProductServices = lazy(() => import('./pages/ProductServices'));
const ReturnRefundPolicy = lazy(() => import('./pages/ReturnRefundPolicy'));
const TermsCondition = lazy(() => import('./pages/TermsCondition'));
const FreeVideCall = lazy(() => import('./pages/Call/VideoCall/FreeVideCall'));
const StripeOnboardingResult = lazy(() => import('./pages/Payment/StripeOnboardingResult'));
const DeleteAccount = lazy(() => import('./pages/DeleteAccount'));
const AcceptableUsePolicy = lazy(() => import('./pages/AcceptableUsePolicy'));
const CommunityGuidelines = lazy(() => import('./pages/CommunityGuidelines'));
const LegalCompliance = lazy(() => import('./pages/LegalCompliance'));

function App() {
  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover={false}
      />
      <BrowserRouter>
        {/* ScrollToTop component */}
        <ScrollToTop />

        <Suspense fallback={<PreLoader Loader={true} />}>
          <Routes>
            {/* ////////////////////// WithOutAuth middleware//////////////////////////// */}
            <Route path='/' element={<WithOutAuth><Home /></WithOutAuth>} />

            <Route path='' element={<BasePage />}>
              {/* ////////////////////// WithOutAuth middleware//////////////////////////// */}
              {/* ========== Auth =========== */}
              <Route path='login' element={<WithOutAuth><Login /></WithOutAuth>} />
              <Route path='register' element={<WithOutAuth><Register /></WithOutAuth>} />
              <Route path='otp' element={<WithOutAuth><OTP /></WithOutAuth>} />
              <Route path='identification' element={<Identification />} />
              <Route path='preferences' element={<Preferences />} />
              <Route path='about-us' element={<WithOutAuth><AboutUs /></WithOutAuth>} />
              <Route path='delete-account' element={<WithOutAuth><DeleteAccount /></WithOutAuth>} />

              <Route path='forgot-password' element={<WithOutAuth><ForgotPassword /></WithOutAuth>} />
              <Route path='forgot-otp' element={<WithOutAuth><ForgotOTP /></WithOutAuth>} />
              <Route path='forgot-new-password' element={<WithOutAuth><NewPassword /></WithOutAuth>} />

              <Route path='disclaimer' element={<Disclaimer />} />
              <Route path='privacy-policy' element={<PrivacyPolicy />} />
              <Route path='product-services' element={<ProductServices />} />
              <Route path='return-refund-policy' element={<ReturnRefundPolicy />} />
              <Route path='terms-condition' element={<TermsCondition />} />
              <Route path='acceptable-use-policy' element={<AcceptableUsePolicy />} />
              <Route path='community-guidelines' element={<CommunityGuidelines />} />
              <Route path='legal-compliance' element={<LegalCompliance />} />
              {/* ========== End Auth =========== */}

              <Route path='contact' element={<Contact />} />
              <Route path='blogs' element={<Blogs />} />

              {/* ////////////////////// RequireAuth middleware//////////////////////////// */}
              <Route path='find-match' element={<RequireAuth><FindMatch /></RequireAuth>} />
              <Route path='profile' element={<RequireAuth><UserProfile /></RequireAuth>} />
              <Route path='view-profile' element={<RequireAuth><ViewProfile /></RequireAuth>} />
              <Route path='chat' element={<RequireAuth><Chat /></RequireAuth>} />
              <Route path='payment' element={<RequireAuth><Payment /></RequireAuth>} />
              <Route path='paypal-payment' element={<RequireAuth><PaypalPayment /></RequireAuth>} />
              <Route path='payment-status' element={<RequireAuth><PaymentStatus /></RequireAuth>} />
              <Route path='stripe-onboarding-result' element={<RequireAuth><StripeOnboardingResult /></RequireAuth>} />
              <Route path='connections' element={<RequireAuth><Connections /></RequireAuth>} />
              <Route path='notification' element={<RequireAuth><Notification /></RequireAuth>} />
              <Route path='gallery' element={<RequireAuth><Gallery /></RequireAuth>} />

              <Route path='video-call' element={<RequireAuth><CallStateCheck><VideoCall /></CallStateCheck></RequireAuth>} />
              <Route path='audio-call' element={<RequireAuth><CallStateCheck><AudioCall /></CallStateCheck></RequireAuth>} />
              <Route path='go-live' element={<RequireAuth><GoLiveStream /></RequireAuth>} />
              <Route path='private-video-room' element={<RequireAuth><PrivateVideoRoomScreen /></RequireAuth>} />

              {/* ======== Not Found page (404) ==== */}
              <Route path='*' element={<NotFound />} />
            </Route>

            <Route path='/free-video-call' element={<FreeVideCall />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
}

export default App;
